// ADR 8.9.26g §3 — the loading placeholder, written here so 8.9.26h's image
// slot inherits it rather than inventing a second one.
//
// It is `role="status"` with `aria-busy`, not a bare grey box: a placeholder
// that says nothing to a screen reader is a page that looks like it finished
// loading and rendered nothing. `aria-live="polite"` so the announcement waits
// for a pause rather than interrupting (AC5).
//
// `bg-surface-sunken` rather than a grey: the recessed fill is exactly what a
// hole in the page should read as, and it inverts correctly under `.dark`
// where a light grey would glow.
//
// ADR 15.9.26af §4 — a shimmer, not a pulse: the same three-stop gradient
// sliding across that Android's `DetailShell.kt` draws (`tween(1500,
// LinearEasing)`). `motion-safe:` so it is opt-in at the call site; the
// reduced-motion rule in `index.css` is the second belt. `bg-[length:…]` is
// an arbitrary *size*, not a colour — 8.9.26g AC4 is untouched.

export interface SkeletonProps {
  /** Sizing is the caller's — a line, a square, an image slot's aspect box. */
  className?: string
  /**
   * What is loading, for assistive tech only. Callers pass a translated
   * string; there is no default, because a hard-coded English one would be the
   * only unlocalised string on the page.
   */
  label?: string
  /** ADR 8.10.26o §5 — a bar inside a region that already announces; no role, no live region. */
  decorative?: boolean
}

export function Skeleton({ className = 'h-4 w-full', label, decorative = false }: SkeletonProps) {
  const live = decorative
    ? { 'aria-hidden': true }
    : { role: 'status', 'aria-busy': true, 'aria-live': 'polite' as const }
  return (
    <div
      {...live}
      data-testid="skeleton"
      className={
        'motion-safe:animate-shimmer rounded bg-gradient-to-r from-surface-sunken ' +
        `via-surface-container to-surface-sunken bg-[length:200%_100%] ${className}`
      }
    >
      {label === undefined || decorative ? null : <span className="sr-only">{label}</span>}
    </div>
  )
}
